import { SelectedTheme } from "@revolt/state/stores/Theme";

/**
 * Generate the Material variables from the given properties
 *
 * Currently only generates color keys
 */
export function createMaterialColourVariables<P extends string>(
  theme: SelectedTheme,
  prefix: P,
): addPrefixToObject<MaterialColours, P> {
  switch (theme.preset) {
    case "you":
      // Locked to the fixed Game Freedom scheme (see PLAYERID_COLOURS). The accent /
      // variant / darkMode inputs are intentionally ignored.
      return Object.entries(PLAYERID_COLOURS).reduce(
        (d, [key, value]) => ({
          ...d,
          [`${prefix}${key}`]: value,
        }),
        {} as addPrefixToObject<MaterialColours, P>,
      );
    default:
      return {} as never;
  }
}

/**
 * Create R,G,B triplets for MDUI variables
 */
export function createMduiColourTriplets<P extends string>(
  theme: SelectedTheme,
  prefix: P,
): addPrefixToObject<MaterialColours, P> {
  const variables = createMaterialColourVariables(theme, prefix);

  for (const key in variables) {
    const [_, r, g, b] = /#([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})/i.exec(
      variables[key as keyof typeof variables] as string,
    )!;

    variables[key as keyof typeof variables] =
      `${parseInt(r, 16)}, ${parseInt(g, 16)}, ${parseInt(b, 16)}` as never;
  }

  return variables;
}

type addPrefixToObject<T, P extends string> = {
  [K in keyof T as K extends string ? `${P}${K}` : never]: T[K];
};

type _addSuffixToObject<T, S extends string> = {
  [K in keyof T as K extends string ? `${K}${S}` : never]: T[K];
};

type MaterialColours = {
  primary: string;
  "on-primary": string;
  "primary-container": string;
  "on-primary-container": string;
  secondary: string;
  "on-secondary": string;
  "secondary-container": string;
  "on-secondary-container": string;
  tertiary: string;
  "on-tertiary": string;
  "tertiary-container": string;
  "on-tertiary-container": string;
  error: string;
  "on-error": string;
  "error-container": string;
  "on-error-container": string;

  "primary-fixed": string;
  "primary-fixed-dim": string;
  "on-primary-fixed": string;
  "on-primary-fixed-variant": string;
  "secondary-fixed": string;
  "secondary-fixed-dim": string;
  "on-secondary-fixed": string;
  "on-secondary-fixed-variant": string;
  "tertiary-fixed": string;
  "tertiary-fixed-dim": string;
  "on-tertiary-fixed": string;
  "on-tertiary-fixed-variant": string;

  "surface-dim": string;
  surface: string;
  "surface-bright": string;

  "surface-container-lowest": string;
  "surface-container-low": string;
  "surface-container": string;
  "surface-container-high": string;
  "surface-container-highest": string;

  "on-surface": string;
  "on-surface-variant": string;
  outline: string;
  "outline-variant": string;

  "inverse-surface": string;
  "inverse-on-surface": string;
  "inverse-primary": string;

  scrim: string;
  shadow: string;
};

/**
 * The Game Freedom palette, taken from the account portal at playerid.me, mapped onto
 * the Material 3 token set. The client is locked to this single scheme.
 * Every value MUST be 6-digit hex - createMduiColourTriplets() parses #RRGGBB into RGB
 * triplets.
 *
 * Two things drive the mapping.
 *
 * First, the surface ramp is deliberately shallow. playerid separates regions with a
 * border (#4A4C55, which carries 117 rules in its stylesheet) rather than with
 * lightness, so its panels sit within ~1.1:1 of each other. Matching it means dividers
 * do the work that elevation does in stock Material, and `outline-variant` matters more
 * here than it does upstream.
 *
 * Second, tokens are assigned by which component consumes them, NOT by M3 elevation
 * semantics, because the chat area has to read as lighter than the sidebar beside it:
 *
 *   surface                  -> SidebarBase       -> page background   #1F2127
 *   surface-container-low    -> Content / chat    -> card              #1B1D24
 *   surface-container-high   -> Layout, UserPanel -> deepest button    #191B20
 *   surface-container        -> floating panels, menus, auth card      #26282F
 *   surface-container-highest-> MessageBox, inputs, UserPanel          #21232A
 *   surface-container-lowest -> ServerList rail                        #16181E
 *
 * That makes the ramp non-monotonic, so surfaces outside the chat window no longer read
 * as a clean elevation ladder. That is the cost of matching the portal and is intended.
 */
const PLAYERID_COLOURS: MaterialColours = {
  // Primary = the gold on the portal's solid buttons (.btn-warning), on the near-black
  // it puts on its gold surfaces. Hover lifts toward the light end of the gradient the
  // portal uses for its primary call to action (#FFE08A -> #E0A800).
  //
  // `primary-container` backs mentions, quotes, reply previews and the file carousel;
  // it is the same gold mixed down over the chat surface so those blocks stay visible
  // without shouting.
  primary: "#DEB103",
  "on-primary": "#2A2100",
  "primary-container": "#5C4E1E",
  "on-primary-container": "#FFE08A",
  // Secondary = the portal's muted grey, for lower-emphasis controls
  secondary: "#4A4C55",
  "on-secondary": "#E7E9EE",
  "secondary-container": "#252730",
  "on-secondary-container": "#E7E9EE",
  // Tertiary = the lighter gold the portal uses for accented text
  tertiary: "#FFD76A",
  "on-tertiary": "#2A2100",
  "tertiary-container": "#252730",
  "on-tertiary-container": "#E7E9EE",
  // Error = the portal's danger red (--bs-danger)
  error: "#F95F53",
  "on-error": "#2A0A07",
  "error-container": "#4A1F1B",
  "on-error-container": "#FFC9C4",

  // Fixed tones (consumed by a few M3 components)
  "primary-fixed": "#FFE08A",
  "primary-fixed-dim": "#FFD76A",
  "on-primary-fixed": "#2A2100",
  "on-primary-fixed-variant": "#8A6E00",
  "secondary-fixed": "#D4D6DC",
  "secondary-fixed-dim": "#4A4C55",
  "on-secondary-fixed": "#16181E",
  "on-secondary-fixed-variant": "#4A4C55",
  "tertiary-fixed": "#FFE9A8",
  "tertiary-fixed-dim": "#FFD76A",
  "on-tertiary-fixed": "#2A2100",
  "on-tertiary-fixed-variant": "#8A6E00",

  // Surfaces - see the mapping table above.
  //
  // Every tone here holds the portal's hue: green sits two points above red, blue
  // seven to nine above green. `surface-container` and `surface-container-highest`
  // used to break that (#212430 and #2E3140 ran twelve and fifteen points of blue
  // over green), which is why the panes they painted read as a different colour
  // rather than a different elevation.
  "surface-dim": "#16181E",
  surface: "#1F2127",
  "surface-bright": "#363945",
  "surface-container-lowest": "#16181E",
  // Content: chat, member list, settings pane. One step over the navigation
  // column beside it rather than the four it used to sit at (#252730), so the
  // two regions read as neighbours and the divider still separates them.
  "surface-container-low": "#1B1D24",
  // Floating panels: context menus, autocomplete, selects, the auth card. One
  // step over the fields, which is all it needs now that these carry a hairline
  // and a shadow; at #2A2C35 it was the brightest thing on screen by some way
  // and a menu read as lit rather than as lying on top.
  "surface-container": "#26282F",
  "surface-container-high": "#191B20",
  // Inputs, the message composer and the user panel. One step over the content
  // they sit on. It was #2F313B, which was a reasonable lift off #252730 but
  // became a jump of twenty points once content settled at #1B1D24, so fields
  // read as lit rather than raised.
  "surface-container-highest": "#21232A",

  // Text + borders. NOTE: in M3 semantics `outline` is a LEGIBLE mid tone - upstream
  // components use it for faint text (category titles, timestamps, resting channel
  // names), so it takes the portal's body colour; `outline-variant` is the border tone
  // and carries most of the region separation in this theme.
  "on-surface": "#E7E9EE",
  "on-surface-variant": "#9AA0AC",
  outline: "#808191",
  // #4A4C55 came straight from the portal, where it sits against surfaces in
  // the 30s and 40s. Ours now run 22 to 38, so the same value was a jump of
  // nearly fifty points and every seam in the app read as a drawn line rather
  // than an edge. This holds the portal's hue and lands about half as far
  // above the surfaces it divides.
  "outline-variant": "#363840",

  // Inverse (snackbars, tooltips)
  "inverse-surface": "#E7E9EE",
  "inverse-on-surface": "#252730",
  "inverse-primary": "#8A6E00",

  scrim: "#000000",
  shadow: "#000000",
};

/**
 * Generate a Material You colour scheme
 * @param accent Accent colour in hex format
 * @param darkMode Dark mode
 * @param constrat Constrast level
 * @returns Material colours
 */
